import { useEffect } from "react";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import { siteInfo } from "@/mocks/site";
import SubmitForm from "./components/SubmitForm";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "网站定位",
    desc: "不做断章取义的八卦搬运，只做可核对的素材归档。把截图、录音、声明放在一起，让读者自己判断，而不是替读者下结论。",
  },
  {
    icon: "ri-plant-line",
    title: "发展理念",
    desc: "吃瓜本身没有门槛，但看懂材料有。我们希望把「对照前后说法」变成一件顺手的事，让讨论回到证据本身。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "内容原则",
    desc: "标注来源、区分实锤与待核实、不公开无关人等隐私。存在多种说法的事件并列呈现，任何一方都有更正与补充的通道。",
  },
];

const testimonials = [
  {
    name: "夜里的橘猫",
    role: "长期读者",
    text: "以前刷到瓜都是一头雾水，这里直接把截图和录音按时间排好，对照着看就清楚多了。",
  },
  {
    name: "阿澈不加糖",
    role: "事件投稿者",
    text: "我补充过一条录音的完整版，他们先私信核对了出处才收录，还标注了来源，这点挺让人放心。",
  },
  {
    name: "周五有风",
    role: "内容从业者",
    text: "同类站点里少见的克制，没有夸张标题，也没有把猜测写成结论，翻起来像在看一份材料清单。",
  },
];

export default function About() {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) {
        window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于 131吃瓜",
      description: siteInfo.description,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "131吃瓜",
      description: "专注整理公开可见素材的吃瓜证据合集站点。",
      email: siteInfo.email,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "线索提交与纠错",
        email: siteInfo.email,
        availableLanguage: ["zh-CN"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 · 131吃瓜证据合集的整理原则"
        description="131吃瓜是专注整理公开可见素材的吃瓜证据合集站点，说明网站定位、发展理念、内容原则与素材更正通道，并长期接收线索补充与纠错反馈。"
        keywords="131吃瓜,关于我们,证据合集,内容原则,线索提交"
        path="/about"
        type="website"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <section className="w-full bg-background-100/40 pt-24 md:pt-32 pb-10 md:pb-14 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
              关于 131吃瓜
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base leading-7 text-foreground-500">
              {siteInfo.since}开始整理，做到今天还是同一件事：把散落的素材收拢、标清来源、按时间排好，让吃瓜这件事少一点猜测。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div className="relative w-full h-64 sm:h-80 lg:h-[420px] rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=calm%20dark%20archive%20desk%20with%20stacked%20folders%20and%20warm%20amber%20lamp%20light%2C%20magnifying%20glass%20resting%20on%20documents%2C%20moody%20editorial%20still%20life%2C%20crimson%20accent%2C%20cinematic%20shadows&width=1200&height=900&seq=chigua-about-01&orientation=landscape"
                alt="暖色灯光下堆叠的档案与放大镜，象征证据整理工作台"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <SectionHeading
                eyebrow="Who we are"
                title="把「听故事」变成「看材料」"
                description="热点来得快，说法也变得快。131吃瓜做的事情其实很朴素：在讨论最热的时候，先把截图、录音、声明按时间顺序摆出来，标清每一条的出处。"
              />
              <p className="mt-5 text-sm md:text-base leading-8 text-foreground-500">
                我们不生产结论，也不替任何一方说话。同一事件如果存在多种说法，会并列呈现，并注明各自的来源与时间。遇到缺少原始出处、存在剪辑痕迹的素材，会明确标注为待核实，而不是当成实锤。
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-500">126+</p>
                  <p className="mt-1 text-xs text-foreground-500">已收录事件档案</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <p className="font-heading text-2xl font-bold text-accent-300">3,480</p>
                  <p className="mt-1 text-xs text-foreground-500">整理完成的证据条目</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/50 py-14 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <SectionHeading
              eyebrow="Principles"
              title="定位、理念与内容原则"
              description="这几条决定了什么会被收录、什么会被标成待核实，也是我们判断素材能不能进合集的标准。"
            />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {principles.map((item) => (
                <article key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-300 text-xl">
                    <i className={item.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-500">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <SectionHeading eyebrow="Voices" title="读者怎么说" align="center" />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((item) => (
                <figure key={item.name} className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-300">
                    <i className="ri-double-quotes-l" />
                  </span>
                  <blockquote className="mt-4 text-sm leading-7 text-foreground-600">{item.text}</blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-background-200/70">
                    <p className="font-heading text-sm font-semibold text-foreground-950">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="submit" className="w-full bg-background-100/50 py-14 md:py-20 scroll-mt-24">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr] gap-8 lg:gap-14">
            <div>
              <SectionHeading
                eyebrow="Contribute"
                title="提交线索 / 联系我们"
                description="补充素材、纠正信息、要求更正或是有合作想法，都可以直接填在这里。"
              />
              <ul className="mt-6 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-300">
                    <i className="ri-mail-line" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">邮箱</p>
                    <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                      {siteInfo.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-300">
                    <i className="ri-time-line" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">回复时间</p>
                    <p className="text-sm text-foreground-500">{siteInfo.replyNote}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-300">
                    <i className="ri-shield-check-line" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">素材处理</p>
                    <p className="text-sm text-foreground-500">先核对来源与时间，再决定是否收录或更正。</p>
                  </div>
                </li>
              </ul>
            </div>
            <SubmitForm />
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}